import { Box, Button, Text, useColorMode } from "theme-ui";
import theme from "../../../../theme";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import { Inputform } from "../../../forms-component/InputForm";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../@types";
import { getchecklistDataById } from "../../../../store/actions/masterData.action";
import { ActionCreateByUrl, ActionUpdateById } from "../../../../store/actions/CommonAction";
import * as Yup from 'yup';
import FormSelect from "../../../forms-component/globalSelect";
import { LuPen, LuTrash } from "react-icons/lu";

interface ChecklistItem {
    id: string;
    label: string;
}

const AddEditChecklist: React.FC<any> = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { checklistId } = useParams();
    const { individualChecklist } = useSelector((state: RootState) => state?.masterData);
    const [inputValue, setInputValue] = useState('');
    const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([]);
    const [isCreating, setIsCreating] = useState(!checklistId);
    const [colorMode] = useColorMode();

    // Drag state refs (no re-render needed during drag)
    const dragIndex = useRef<number | null>(null);
    const dragOverIndex = useRef<number | null>(null);
    const [dragOver, setDragOver] = useState<number | null>(null);

    // Inline edit state
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editValue, setEditValue] = useState('');

    const isDark = colorMode !== "light";
    const containerBg = isDark ? '#282e42' : '#ffffff';
    const borderColor = isDark ? '#3d4460' : '#f7efe5';
    const textColor = isDark ? '#ffffff' : '#1a1a2e';
    const itemBg = isDark ? '#1e2336' : '#ffffff';
    const itemBgHover = isDark ? '#2d3452' : '#eef0ff';
    const dragHandleColor = isDark ? '#6b7280' : '#9ca3af';

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            templateName: isCreating ? '' : (individualChecklist?.data?.templateName || ''),
            checklistItem: isCreating ? [] as string[] : (individualChecklist?.data?.checklistItem || []),
            showInActions: isCreating ? '' : (individualChecklist?.data?.showInActions || ''),
        },
        validationSchema: Yup.object().shape({
            templateName: Yup.string().required('Template Name is required'),
            checklistItem: isCreating ? Yup.array()
                .of(Yup.string().required('Each checklist item must be a non-empty string'))
                .min(1, 'At least one checklist item is required')
                .required('Checklist Item is required') : Yup.array().notRequired(),
            showInActions: Yup.string().required('Show in Actions is required'),
        }),
        onSubmit: (values, { resetForm }) => {
            if (checklistId) {
                dispatch(ActionUpdateById({ Query: `checklist-templates-routes/editChecklist/${checklistId}`, obj: values }, (res: any) => {
                    if (res.status) { navigate("/checklists"); resetForm(); }
                }));
            } else {
                dispatch(ActionCreateByUrl({ Query: "checklist-templates-routes/createChecklistTemplates", obj: values }, (res: any) => {
                    if (res.status) { navigate("/checklists"); resetForm(); }
                }));
            }
        },
    });

    const { handleSubmit, setFieldValue, setValues, values, handleBlur, touched, errors, dirty } = formik;

    useEffect((): void => {
        // Clear stale local state immediately so old data never flashes
        setChecklistItems([]);
        formik.resetForm({ values: { templateName: '', checklistItem: [], showInActions: "" } });

        if (checklistId) {
            setIsCreating(false);
            dispatch(getchecklistDataById({ id: checklistId }));
        } else {
            setIsCreating(true);
        }
    }, [checklistId]);

    useEffect(() => {
        if (individualChecklist?.data?._id === checklistId && checklistId) {
            const loaded: ChecklistItem[] = (individualChecklist?.data?.checklistItem || []).map(
                (item: string, idx: number) => ({ id: `item-${idx}-${Date.now()}`, label: item })
            );
            // Use resetForm so initialValues are also updated — this makes dirty=false
            // until the user actually changes something (vs setValues which sets dirty=true immediately)
            formik.resetForm({
                values: {
                    templateName: individualChecklist?.data?.templateName || '',
                    checklistItem: loaded.map((i) => i.label),
                    showInActions: individualChecklist?.data?.showInActions || '',
                },
            });
            setChecklistItems(loaded);
        }
    }, [individualChecklist]);

    const showInActions = [
        { label: "Todos", value: "todos" },
        { label: "Subtasks", value: "subtasks" },
    ];

    // ── Add item on Enter ──────────────────────────────────────────
    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const trimmed = inputValue.trim();
            if (!trimmed) return;
            const newItem: ChecklistItem = { id: `item-${Date.now()}`, label: trimmed };
            const updated = [...checklistItems, newItem];
            setChecklistItems(updated);
            setFieldValue('checklistItem', updated.map((i) => i.label));
            setInputValue('');
        }
    };

    // ── Remove item ────────────────────────────────────────────────
    const removeItem = (id: string) => {
        const updated = checklistItems.filter((i) => i.id !== id);
        setChecklistItems(updated);
        setFieldValue('checklistItem', updated.map((i) => i.label));
    };

    // ── Inline edit ────────────────────────────────────────────────
    const startEdit = (item: ChecklistItem) => {
        setEditingId(item.id);
        setEditValue(item.label);
    };

    const saveEdit = (id: string) => {
        const trimmed = editValue.trim();
        if (!trimmed) return;
        const updated = checklistItems.map((i) => (i.id === id ? { ...i, label: trimmed } : i));
        setChecklistItems(updated);
        setFieldValue('checklistItem', updated.map((i) => i.label));
        setEditingId(null);
        setEditValue('');
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditValue('');
    };

    // ── Drag & Drop ────────────────────────────────────────────────
    const handleDragStart = (index: number) => {
        dragIndex.current = index;
    };

    const handleDragEnter = (index: number) => {
        dragOverIndex.current = index;
        setDragOver(index);
    };

    const handleDrop = () => {
        if (dragIndex.current === null || dragOverIndex.current === null) return;
        if (dragIndex.current === dragOverIndex.current) {
            dragIndex.current = null;
            dragOverIndex.current = null;
            setDragOver(null);
            return;
        }
        const reordered = [...checklistItems];
        const [moved] = reordered.splice(dragIndex.current, 1);
        reordered.splice(dragOverIndex.current, 0, moved);
        setChecklistItems(reordered);
        setFieldValue('checklistItem', reordered.map((i) => i.label));
        dragIndex.current = null;
        dragOverIndex.current = null;
        setDragOver(null);
    };

    const handleDragEnd = () => {
        dragIndex.current = null;
        dragOverIndex.current = null;
        setDragOver(null);
    };

    return (
        <div className="main-content p-5">
            <form onSubmit={handleSubmit}>
                <h1 style={{ color: colorMode === "light" ? "black" : "white" }}>
                    {checklistId ? "Edit" : "Add"} Checklist
                </h1>

                <div className="row">
                    {/* Template Name */}
                    <div className="col-md-6 mt-4">
                        <Inputform
                            label={"Template Name"}
                            Placeholder={"Enter template Name"}
                            Name={"templateName"}
                            type='text'
                            sx={{ color: colorMode === "light" ? "black" : "white" }}
                            Title_sx={{ color: "grey3" }}
                            touched={touched?.templateName}
                            asterisk={true}
                            errors={errors?.templateName}
                            values={values?.templateName || ''}
                            Max_Length={10}
                            onBlur={handleBlur}
                            OnChange={(event: any) => {
                                setFieldValue("templateName", event.target.value, true);
                            }}
                        />
                    </div>

                    {/* Select Action */}
                    <Box className="col-md-6 mt-4">
                        <FormSelect
                            label={"Select Action"}
                            value={values?.showInActions}
                            placeholder={"Select Actions"}
                            options={showInActions || []}
                            sx={{ color: colorMode === "light" ? "secondary" : "white" }}
                            isMulti={false}
                            DefultSelect='Select Todo Assignee'
                            touched={touched?.showInActions}
                            errors={errors?.showInActions}
                            asterisk={true}
                            onBlur={handleBlur}
                            Name='showInActions'
                            onChange={(teamLeader: any) => {
                                setFieldValue('showInActions', teamLeader?.value, true);
                            }}
                        />
                    </Box>

                    {/* Checklist Items */}
                    <div className="col-md-12 mt-4">
                        <Text
                            variant="SelectLabel"
                            sx={{ color: colorMode === "light" ? "secondary" : "white", display: 'block', mb: 2 }}
                        >
                            Checklist Items<span className="mx-1 text-danger">*</span>
                        </Text>

                        {/* Input field */}
                        <div style={{ position: 'relative', marginBottom: '12px' }}>
                            <input
                                type="text"
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyDown={handleInputKeyDown}
                                placeholder="Type a checklist item and press Enter to add..."
                                style={{
                                    width: '100%',
                                    padding: '10px 60px 10px 16px',
                                    borderRadius: '10px',
                                    border: `1.5px solid ${borderColor}`,
                                    background: containerBg,
                                    color: textColor,
                                    fontSize: '14px',
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                    transition: 'border-color 0.2s',
                                }}
                                onFocus={(e) => { e.currentTarget.style.borderColor = '#7c5cbf'; }}
                                onBlur={(e) => { e.currentTarget.style.borderColor = borderColor; }}
                            />
                            <span style={{
                                position: 'absolute',
                                right: '14px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                fontSize: '11px',
                                color: dragHandleColor,
                                pointerEvents: 'none',
                            }}>
                                ↵ Enter
                            </span>
                        </div>

                        {/* Drag & Drop List */}
                        {checklistItems.length > 0 && (
                            <div style={{
                                border: `1px solid ${borderColor}`,
                                borderRadius: '12px',
                                background: containerBg,
                                padding: '8px',
                                minHeight: '60px',
                                maxHeight: '320px',
                                overflowY: 'auto',
                            }}>
                                {checklistItems.map((item, index) => {
                                    const isEditing = editingId === item.id;
                                    return (
                                        <div
                                            key={item.id}
                                            draggable={!isEditing}
                                            onDragStart={() => { if (!isEditing) handleDragStart(index); }}
                                            onDragEnter={() => { if (!isEditing) handleDragEnter(index); }}
                                            onDragOver={(e) => e.preventDefault()}
                                            onDrop={!isEditing ? handleDrop : undefined}
                                            onDragEnd={!isEditing ? handleDragEnd : undefined}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px',
                                                padding: '8px 10px',
                                                marginBottom: index < checklistItems.length - 1 ? '6px' : '0',
                                                borderRadius: '8px',
                                                background: isEditing ? itemBgHover : dragOver === index ? itemBgHover : itemBg,
                                                border: isEditing
                                                    ? '1.5px solid #7c5cbf'
                                                    : dragOver === index
                                                        ? '1.5px dashed #7c5cbf'
                                                        : `1px solid ${borderColor}`,
                                                cursor: isEditing ? 'default' : 'grab',
                                                transition: 'background 0.15s, border 0.15s',
                                                userSelect: 'none',
                                            }}
                                        >
                                            {/* Drag handle */}
                                            <span style={{
                                                color: dragHandleColor,
                                                fontSize: '16px',
                                                flexShrink: 0,
                                                cursor: isEditing ? 'default' : 'grab',
                                                opacity: isEditing ? 0.3 : 1,
                                            }}>
                                                ⠿
                                            </span>


                                            {/* Label or inline edit input */}
                                            {isEditing ? (
                                                <input
                                                    autoFocus
                                                    value={editValue}
                                                    onChange={(e) => setEditValue(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') { e.preventDefault(); saveEdit(item.id); }
                                                        if (e.key === 'Escape') cancelEdit();
                                                    }}
                                                    style={{
                                                        flex: 1,
                                                        fontSize: '13px',
                                                        padding: '4px 8px',
                                                        borderRadius: '6px',
                                                        border: '1px solid #7c5cbf',
                                                        background: containerBg,
                                                        color: textColor,
                                                        outline: 'none',
                                                    }}
                                                />
                                            ) : (
                                                <span style={{
                                                    flex: 1,
                                                    fontSize: '13px',
                                                    color: textColor,
                                                    wordBreak: 'break-word',
                                                }}>
                                                    {item.label}
                                                </span>
                                            )}

                                            {/* Action buttons */}
                                            {isEditing ? (
                                                <>
                                                    {/* Save */}
                                                    <button
                                                        type="button"
                                                        title="Save"
                                                        onClick={() => saveEdit(item.id)}
                                                        style={{
                                                            background: '#7c5cbf',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            cursor: 'pointer',
                                                            color: '#fff',
                                                            fontSize: '14px',
                                                            padding: '3px 9px',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        ✓
                                                    </button>
                                                    {/* Cancel */}
                                                    <button
                                                        type="button"
                                                        title="Cancel"
                                                        onClick={cancelEdit}
                                                        style={{
                                                            background: 'none',
                                                            border: `1px solid ${borderColor}`,
                                                            borderRadius: '6px',
                                                            cursor: 'pointer',
                                                            color: dragHandleColor,
                                                            fontSize: '14px',
                                                            padding: '3px 9px',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        ✕
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    {/* Edit button */}
                                                    <button
                                                        type="button"
                                                        title="Edit item"
                                                        onClick={() => startEdit(item)}
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            color: '#7c5cbf',
                                                            fontSize: '18px',
                                                            lineHeight: 1,
                                                            padding: '0 3px',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        <LuPen />

                                                    </button>
                                                    {/* Delete button */}
                                                    <button
                                                        type="button"
                                                        title="Delete item"
                                                        onClick={() => removeItem(item.id)}
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            color: '#e05c5c',
                                                            fontSize: '18px',
                                                            lineHeight: 1,
                                                            padding: '0 3px',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        <LuTrash />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Empty state */}
                        {checklistItems.length === 0 && (
                            <div style={{
                                border: `1px dashed ${borderColor}`,
                                borderRadius: '12px',
                                padding: '24px',
                                textAlign: 'center',
                                color: dragHandleColor,
                                fontSize: '13px',
                            }}>
                                No items yet — type above and press Enter to add
                            </div>
                        )}

                        {errors?.checklistItem && touched?.checklistItem && (
                            <Text variant="Inter15Regular30lh" className="text-danger" sx={{ my: '5px', minHeight: '11px', lineHeight: '10px' }}>
                                {errors?.checklistItem as string}
                            </Text>
                        )}
                    </div>
                </div>

                {/* Action Buttons */}
                <div className='row mt-5'>
                    <div className='col-md-4'></div>
                    <div className='col-md-4'>
                        <Button
                            type="reset"
                            sx={{
                                border: `1px solid ${theme.colors?.primary || 'defaultBorderColor'}`,
                                background: 'white',
                                color: theme.colors?.muted,
                                borderRadius: '12px',
                                padding: [2, 4, 2, 4],
                                width: '95%',
                            }}
                            onClick={() => navigate(-1)}
                        >
                            Cancel
                        </Button>
                    </div>
                    <div className='col-md-4'>
                        <Button
                            sx={{ borderRadius: '12px', padding: [2, 4, 2, 4], width: '95%', opacity: dirty ? 1 : 0.5, cursor: dirty ? 'pointer' : 'not-allowed' }}
                            type='submit'
                            disabled={!dirty}
                        >
                            Submit
                        </Button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default AddEditChecklist;